import React from 'react';
import { Transition } from '@odin-labs/components';
import { cn } from 'utils';
import { setRefFactory } from 'components/utils';
import { QrCodeScannerArgs, useQrCodeScanner } from './useQrCodeScanner';

export type QrCodeScannerProps = Omit<QrCodeScannerArgs, 'isEnabled'> & {
  error?: React.ReactNode;
  isOpen: boolean;
  close: () => void;
};

const classes = {
  transition: cn(
    'odin-space-y-4 odin-overflow-hidden odin-max-h-[600px] odin-transition-all',
    'odin-duration-500 odin-ease-out',
    'data-[closed]:odin-opacity-0 data-[closed]:odin-scale-95 data-[closed]:odin-max-h-0',
  ),
  description: 'odin-text-sm odin-text-gray-600',
  error:
    'odin-rounded-md odin-bg-red-50 odin-p-3 odin-text-sm odin-text-red-700',
  scannerFrame:
    'odin-relative odin-overflow-hidden odin-rounded-lg odin-bg-gray-950',
  scannerGuide:
    'odin-pointer-events-none odin-absolute odin-inset-6 odin-rounded-lg odin-border-2 odin-border-white/80',
  spinnerHolder:
    'odin-absolute odin-inset-0 odin-z-10 odin-flex odin-items-center odin-justify-center odin-bg-gray-900/80',
  spinner:
    'odin-h-10 odin-w-10 odin-animate-spin odin-rounded-full odin-border-4 odin-border-gray-300 odin-border-t-odin-primary',
  video: 'odin-h-[320px] odin-w-full odin-object-cover',
};

export const QrCodeScanner = React.forwardRef<
  HTMLVideoElement,
  QrCodeScannerProps
>((props, ref): React.ReactElement => {
  const {
    cameraFacingMode = 'environment',
    error,
    isOpen,
    close,
    onDetected,
    onError,
  } = props;

  const {
    videoRef,
    isInitializing,
    error: scannerError,
  } = useQrCodeScanner({
    cameraFacingMode,
    isEnabled: isOpen,
    onDetected,
    onError,
  });

  const setRef = setRefFactory<HTMLVideoElement>({
    outerRef: ref,
    innerRef: videoRef,
  });

  return (
    <Transition as="div" className={classes.transition} show={isOpen} appear>
      <p className={classes.description}>
        Scan the QR code next to the badge number on the back of the card, or
        &nbsp;
        <a
          href=""
          className="odin-underline"
          onClick={(ev): void => {
            close();
            ev.preventDefault();
          }}
        >
          enter the value manually
        </a>
        .
      </p>
      <div className={classes.scannerFrame}>
        {isInitializing && (
          <div className={classes.spinnerHolder}>
            <div className={classes.spinner} />
          </div>
        )}
        <video ref={setRef} className={classes.video} muted />
        <div className={classes.scannerGuide} />
      </div>
      {(error || scannerError) && (
        <div className={classes.error}>{error ?? scannerError.message}</div>
      )}
    </Transition>
  );
});

QrCodeScanner.displayName = 'QrCodeScanner';
